import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import '../css/CardConfigPanel.css';

/** Normalizza proprietà da API (camelCase o PascalCase) */
const readCardFlags = (card) => {
  const isQty = card.isQuantity === true || card.IsQuantity === true;
  const divideRaw = card.divideBy1000 ?? card.DivideBy1000;
  return {
    isQuantity: isQty,
    divideBy1000: divideRaw !== false,
  };
};

const buildCardPayload = (form) => {
  const isQty = form.isQuantity === true;
  return {
    name: form.name.trim(),
    infoDescription: form.infoDescription ?? '',
    totalRowLabel: form.totalRowLabel ?? '',
    isQuantity: isQty,
    divideBy1000: !!form.divideBy1000,
  };
};

const CardConfigPanel = ({ isOpen, onClose, onConfigChanged }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [showAddRowForm, setShowAddRowForm] = useState(null); // CardId per cui mostrare il form
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Traccia se ci sono modifiche non salvate
  const [pendingRows, setPendingRows] = useState({}); // { cardId: [righe temporanee] }
  const panelRef = useRef(null); // Ref per il pannello per gestire lo scroll
  const scrollPositionRef = useRef(0); // Ref per mantenere la posizione dello scroll
  const expandedCardsRef = useRef(new Set()); // Ref per mantenere le card espanse

  // Form states
  const [cardForm, setCardForm] = useState({ name: '', infoDescription: '', totalRowLabel: '', divideBy1000: true, isQuantity: false });
  const [rowForm, setRowForm] = useState({ description: '', isInTotal: false, refUrl: '' });

  useEffect(() => {
    if (isOpen) {
      loadCards();
      setHasUnsavedChanges(false); // Reset quando si apre il pannello
    }
  }, [isOpen]);

  const loadCards = async (preserveState = false) => {
    setLoading(true);
    
    // Salva lo stato corrente se necessario
    if (preserveState && panelRef.current) {
      scrollPositionRef.current = panelRef.current.scrollTop;
      expandedCardsRef.current = new Set(expandedCards);
    }
    
    try {
      const response = await axios.get(buildApiUrl('card-config/cards'), {
        params: { includeDeleted: true }
      });
      if (response.data.status === 'OK') {
        setCards(response.data.data);
        
        // Ripristina lo stato dopo il render
        if (preserveState) {
          // Ripristina le card espanse
          setExpandedCards(expandedCardsRef.current);
          
          // Ripristina lo scroll dopo che il DOM è stato aggiornato
          setTimeout(() => {
            if (panelRef.current) {
              panelRef.current.scrollTop = scrollPositionRef.current;
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('Errore durante il caricamento delle card:', error);
      alert('Errore durante il caricamento delle card');
    } finally {
      setLoading(false);
    }
  };

  const toggleCardExpanded = (cardId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };

  const handleCreateCard = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(buildApiUrl('card-config/card'), buildCardPayload(cardForm));
      if (response.data.status === 'OK') {
        await loadCards();
        setCardForm({ name: '', infoDescription: '', totalRowLabel: '', divideBy1000: true, isQuantity: false });
        setShowAddCardForm(false);
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante la creazione della card:', error);
      alert('Errore durante la creazione della card');
    }
  };

  const handleUpdateCard = async (cardId, e) => {
    e.preventDefault();
    try {
      const response = await axios.put(buildApiUrl(`card-config/card/${cardId}`), buildCardPayload(cardForm));
      if (response.data.status === 'OK') {
        await loadCards();
        setEditingCard(null);
        setCardForm({ name: '', infoDescription: '', totalRowLabel: '', divideBy1000: true, isQuantity: false });
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento della card:', error);
      alert('Errore durante l\'aggiornamento della card');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Sei sicuro di voler nascondere questa card? La card verrà nascosta (soft delete) e non verrà più mostrata nel dashboard.')) {
      return;
    }
    try {
      const response = await axios.delete(buildApiUrl(`card-config/card/${cardId}`));
      if (response.data.status === 'OK') {
        await loadCards();
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione della card:', error);
      alert('Errore durante l\'eliminazione della card');
    }
  };

  const handleRestoreCard = async (cardId) => {
    try {
      const response = await axios.post(buildApiUrl(`card-config/card-restore/${cardId}`));
      if (response.data.status === 'OK') {
        await loadCards();
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante il ripristino della card:', error);
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Errore durante il ripristino della card';
      alert(errorMessage);
    }
  };

  const handleCreateRow = async (cardId, e) => {
    e.preventDefault();
    try {
      const response = await axios.post(buildApiUrl('card-config/row'), {
        ...rowForm,
        dimCardId: cardId
      });
      if (response.data.status === 'OK') {
        await loadCards();
        setRowForm({ description: '', isInTotal: false, refUrl: '' });
        setShowAddRowForm(null);
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante la creazione della riga:', error);
      alert('Errore durante la creazione della riga');
    }
  };

  const handleUpdateRow = async (rowId, e) => {
    e.preventDefault();
    try {
      const response = await axios.put(buildApiUrl(`card-config/row/${rowId}`), rowForm);
      if (response.data.status === 'OK') {
        await loadCards();
        setEditingRow(null);
        setRowForm({ description: '', isInTotal: false, refUrl: '' });
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento della riga:', error);
      alert('Errore durante l\'aggiornamento della riga');
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Sei sicuro di voler nascondere questa riga? Non verrà più mostrata e non genererà errori durante l\'import.')) {
      return;
    }
    try {
      const response = await axios.delete(buildApiUrl(`card-config/row/${rowId}`));
      if (response.data.status === 'OK') {
        await loadCards();
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione della riga:', error);
      alert('Errore durante l\'eliminazione della riga');
    }
  };

  const handleRestoreRow = async (rowId) => {
    try {
      const response = await axios.post(buildApiUrl(`card-config/row/${rowId}/restore`));
      if (response.data.status === 'OK') {
        await loadCards();
        if (onConfigChanged) onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante il ripristino della riga:', error);
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Errore durante il ripristino della riga';
      alert(errorMessage);
    }
  };

  const handleAddRowToPending = (cardId, e) => {
    e.preventDefault();
    if (!rowForm.description.trim()) {
      alert('La descrizione è obbligatoria');
      return;
    }

    // Aggiungi la riga alla lista temporanea
    const newRow = {
      description: rowForm.description.trim(),
      isInTotal: rowForm.isInTotal,
      refUrl: rowForm.refUrl?.trim() || null,
      tempId: Date.now() + Math.random() // ID temporaneo univoco
    };

    setPendingRows(prev => ({
      ...prev,
      [cardId]: [...(prev[cardId] || []), newRow]
    }));

    // Reset del form per aggiungere un'altra riga
    setRowForm({ description: '', isInTotal: false, refUrl: '' });
  };

  const handleRemovePendingRow = (cardId, tempId) => {
    setPendingRows(prev => ({
      ...prev,
      [cardId]: (prev[cardId] || []).filter(r => r.tempId !== tempId)
    }));
  };

  const handleSavePendingRows = async (cardId) => {
    const rowsToSave = pendingRows[cardId] || [];
    if (rowsToSave.length === 0) {
      return;
    }

    try {
      setLoading(true);
      
      // Salva tutte le righe in sequenza
      const savePromises = rowsToSave.map(row => 
        axios.post(buildApiUrl('card-config/row'), {
          description: row.description,
          dimCardId: cardId,
          isInTotal: row.isInTotal,
          refUrl: row.refUrl
        })
      );

      await Promise.all(savePromises);

      // Rimuovi le righe temporanee e chiudi il form
      setPendingRows(prev => {
        const updated = { ...prev };
        delete updated[cardId];
        return updated;
      });
      setShowAddRowForm(null);
      setRowForm({ description: '', isInTotal: false, refUrl: '' });

      // Ricarica le card
      await loadCards();
      
      if (onConfigChanged) {
        onConfigChanged();
      }
    } catch (error) {
      console.error('Errore durante il salvataggio delle righe:', error);
      alert('Errore durante il salvataggio delle righe: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPendingRows = (cardId) => {
    setPendingRows(prev => {
      const updated = { ...prev };
      delete updated[cardId];
      return updated;
    });
    setShowAddRowForm(null);
    setRowForm({ description: '', isInTotal: false, refUrl: '' });
  };

  const handleMoveRow = async (cardId, rowId, direction) => {
    try {
      // Trova la card e le sue righe
      const card = cards.find(c => c.id === cardId);
      if (!card) return;

      // Filtra solo le righe non eliminate e ordina per order
      const visibleRows = card.rows
        .filter(r => !r.isDeleted)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      // Trova l'indice della riga corrente
      const currentIndex = visibleRows.findIndex(r => r.id === rowId);
      if (currentIndex === -1) return;

      // Calcola il nuovo indice
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= visibleRows.length) return;

      // Scambia le righe nell'array
      [visibleRows[currentIndex], visibleRows[newIndex]] = [visibleRows[newIndex], visibleRows[currentIndex]];

      // Prepara l'array di ID nell'ordine corretto
      const rowIds = visibleRows.map(r => r.id);

      // Aggiorna lo stato locale immediatamente per feedback visivo
      setCards(prevCards => {
        return prevCards.map(c => {
          if (c.id === cardId) {
            const updatedRows = c.rows.map(r => {
              const newOrder = rowIds.indexOf(r.id);
              return newOrder !== -1 ? { ...r, order: newOrder } : r;
            }).sort((a, b) => {
              // Ordina: prima le righe non eliminate (per order), poi quelle eliminate (per id)
              if (a.isDeleted && !b.isDeleted) return 1;
              if (!a.isDeleted && b.isDeleted) return -1;
              if (!a.isDeleted && !b.isDeleted) {
                return (a.order || 0) - (b.order || 0);
              }
              return a.id - b.id;
            });
            return { ...c, rows: updatedRows };
          }
          return c;
        });
      });

      // Marca che ci sono modifiche non salvate
      setHasUnsavedChanges(true);

      // Chiama l'API per aggiornare l'ordine in background (senza ricaricare)
      // Le modifiche sono già visibili nello stato locale
      try {
        const response = await axios.post(buildApiUrl('card-config/rows-reorder'), {
          rowIds: rowIds
        });

        if (response.data.status !== 'OK') {
          // In caso di errore, ricarica per sincronizzare
          await loadCards();
          setHasUnsavedChanges(false);
          alert('Errore durante il salvataggio dell\'ordine: ' + (response.data.message || 'Errore sconosciuto'));
        }
        // Se OK, non ricaricare - lo stato locale è già aggiornato, ma segna che ci sono modifiche
      } catch (apiError) {
        console.error('Errore durante il salvataggio dell\'ordine:', apiError);
        // In caso di errore, ricarica per sincronizzare
        await loadCards();
        setHasUnsavedChanges(false);
        alert('Errore durante il salvataggio dell\'ordine: ' + (apiError.response?.data?.error?.message || apiError.message));
      }
    } catch (error) {
      console.error('Errore durante lo spostamento della riga:', error);
      alert('Errore durante lo spostamento della riga: ' + (error.response?.data?.error?.message || error.message));
      await loadCards();
    }
  };

  const startEditCard = (card) => {
    setEditingCard(card.id);
    const flags = readCardFlags(card);
    setCardForm({
      name: card.name,
      infoDescription: card.infoDescription || '',
      totalRowLabel: card.totalRowLabel || '',
      ...flags
    });
  };

  const startEditRow = (row) => {
    setEditingRow(row.id);
    setRowForm({
      description: row.description,
      isInTotal: row.isInTotal,
      refUrl: row.refUrl || ''
    });
  };

  const cancelEdit = () => {
    setEditingCard(null);
    setEditingRow(null);
    setCardForm({ name: '', infoDescription: '', totalRowLabel: '', divideBy1000: true, isQuantity: false });
    setRowForm({ description: '', isInTotal: false, refUrl: '' });
    setShowAddCardForm(false);
    setShowAddRowForm(null);
  };

  const handleSave = async () => {
    try {
      // Salva lo stato corrente prima di ricaricare
      if (panelRef.current) {
        scrollPositionRef.current = panelRef.current.scrollTop;
      }
      expandedCardsRef.current = new Set(expandedCards);
      
      // Ricarica le card dal server
      setLoading(true);
      const response = await axios.get(buildApiUrl('card-config/cards'), {
        params: { includeDeleted: true }
      });
      
      if (response.data.status === 'OK') {
        setCards(response.data.data);
        
        // Ripristina lo stato dopo il render
        setExpandedCards(expandedCardsRef.current);
        
        // Ripristina lo scroll dopo che il DOM è stato aggiornato
        setTimeout(() => {
          if (panelRef.current) {
            panelRef.current.scrollTop = scrollPositionRef.current;
          }
        }, 150);
        
        setHasUnsavedChanges(false);
        
        // Notifica il cambio per aggiornare il dashboard
        if (onConfigChanged) {
          onConfigChanged();
        }
      }
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      alert('Errore durante il salvataggio: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="card-config-overlay" onClick={onClose}>
      <div className="card-config-panel" ref={panelRef} onClick={(e) => e.stopPropagation()}>
        <div className="card-config-header">
          <h2>Configurazione Card</h2>
          <button className="card-config-close" onClick={onClose}>×</button>
        </div>

        <div className="card-config-content">
          {loading ? (
            <div className="card-config-loading">Caricamento...</div>
          ) : (
            <>
              <div className="card-config-actions">
                <button 
                  className="btn-add-card" 
                  onClick={() => {
                    setShowAddCardForm(true);
                    setCardForm({ name: '', infoDescription: '', totalRowLabel: '', divideBy1000: true, isQuantity: false });
                  }}
                >
                  + Aggiungi Card
                </button>
              </div>

              {showAddCardForm && (
                <div className="card-config-form">
                  <h3>Nuova Card</h3>
                  <form onSubmit={handleCreateCard}>
                    <div className="form-group">
                      <label>Nome Card *</label>
                      <input
                        type="text"
                        value={cardForm.name}
                        onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Descrizione</label>
                      <textarea
                        value={cardForm.infoDescription}
                        onChange={(e) => setCardForm({ ...cardForm, infoDescription: e.target.value })}
                        rows="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Etichetta Totale</label>
                      <input
                        type="text"
                        value={cardForm.totalRowLabel}
                        onChange={(e) => setCardForm({ ...cardForm, totalRowLabel: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tipo valori</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="valueMode-new"
                            checked={!cardForm.isQuantity}
                            onChange={() => setCardForm((prev) => ({ ...prev, isQuantity: false }))}
                          />
                          Euro (€)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="valueMode-new"
                            checked={cardForm.isQuantity}
                            onChange={() => setCardForm((prev) => ({ ...prev, isQuantity: true }))}
                          />
                          Quantità (Qty)
                        </label>
                      </div>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={cardForm.divideBy1000}
                          onChange={(e) => setCardForm({ ...cardForm, divideBy1000: e.target.checked })}
                        />
                        Dividi i valori per 1000 (€/1000 o Qty/1000)
                      </label>
                    </div>
                    <div className="form-actions">
                      <button type="submit">Crea</button>
                      <button type="button" onClick={cancelEdit}>Annulla</button>
                    </div>
                  </form>
                </div>
              )}

              <div className="cards-list">
                {cards.map(card => {
                  const vf = readCardFlags(card);
                  return (
                  <div key={card.id} className={`card-item ${card.isDeleted ? 'card-deleted' : ''}`}>
                    <div className="card-header">
                      <button 
                        className="card-expand" 
                        onClick={() => toggleCardExpanded(card.id)}
                      >
                        {expandedCards.has(card.id) ? '▼' : '▶'}
                      </button>
                      <span className="card-name">
                        {card.name}
                        {card.isDeleted && <span className="badge-deleted"> (Nascosta)</span>}
                      </span>
                      <div className="card-actions">
                        {editingCard === card.id ? (
                          <form onSubmit={(e) => handleUpdateCard(card.id, e)} className="inline-form">
                            <input
                              type="text"
                              value={cardForm.name}
                              onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                              required
                              className="inline-input"
                            />
                            <button type="submit">Salva</button>
                            <button type="button" onClick={cancelEdit}>Annulla</button>
                          </form>
                        ) : (
                          <>
                            {!card.isDeleted && (
                              <>
                                <button onClick={() => startEditCard(card)}>Modifica</button>
                                <button onClick={() => handleDeleteCard(card.id)} className="btn-delete">Nascondi</button>
                              </>
                            )}
                            {card.isDeleted && (
                              <button onClick={() => handleRestoreCard(card.id)} className="btn-restore">Ripristina</button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {expandedCards.has(card.id) && (
                      <div className="card-details">
                        {editingCard === card.id ? (
                          <form onSubmit={(e) => handleUpdateCard(card.id, e)}>
                            <div className="form-group">
                              <label>Descrizione</label>
                              <textarea
                                value={cardForm.infoDescription}
                                onChange={(e) => setCardForm({ ...cardForm, infoDescription: e.target.value })}
                                rows="3"
                              />
                            </div>
                            <div className="form-group">
                              <label>Etichetta Totale</label>
                              <input
                                type="text"
                                value={cardForm.totalRowLabel}
                                onChange={(e) => setCardForm({ ...cardForm, totalRowLabel: e.target.value })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Tipo valori</label>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                  <input
                                    type="radio"
                                    name={`valueMode-${card.id}`}
                                    checked={!cardForm.isQuantity}
                                    onChange={() => setCardForm((prev) => ({ ...prev, isQuantity: false }))}
                                  />
                                  Euro (€)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                  <input
                                    type="radio"
                                    name={`valueMode-${card.id}`}
                                    checked={cardForm.isQuantity}
                                    onChange={() => setCardForm((prev) => ({ ...prev, isQuantity: true }))}
                                  />
                                  Quantità (Qty)
                                </label>
                              </div>
                            </div>
                            <div className="form-group">
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={cardForm.divideBy1000}
                                  onChange={(e) => setCardForm({ ...cardForm, divideBy1000: e.target.checked })}
                                />
                                Dividi i valori per 1000 (€/1000 o Qty/1000)
                              </label>
                            </div>
                            <div className="form-actions">
                              <button type="submit">Salva</button>
                              <button type="button" onClick={cancelEdit}>Annulla</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            {card.infoDescription && (
                              <div className="card-info">
                                <strong>Descrizione:</strong> {card.infoDescription}
                              </div>
                            )}
                            {card.totalRowLabel && (
                              <div className="card-info">
                                <strong>Etichetta Totale:</strong> {card.totalRowLabel}
                              </div>
                            )}
                            <div className="card-info">
                              <strong>Valori:</strong>{' '}
                              {vf.isQuantity
                                ? (vf.divideBy1000 ? 'Quantità (Qty/1000)' : 'Quantità (Qty)')
                                : (vf.divideBy1000 ? '€/1000 (migliaia)' : '€ (interi)')}
                            </div>
                            <div className="rows-section">
                              <div className="rows-header">
                                <h4>Righe ({card.rows.length})</h4>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  {hasUnsavedChanges && (
                                    <button 
                                      className="btn-save-inline" 
                                      onClick={handleSave}
                                      style={{ backgroundColor: '#20362C', color: 'white', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                      💾 Salva modifiche
                                    </button>
                                  )}
                                  <button 
                                    onClick={() => {
                                      setShowAddRowForm(card.id);
                                      setRowForm({ description: '', isInTotal: false, refUrl: '' });
                                    }}
                                  >
                                    + Aggiungi Riga
                                  </button>
                                </div>
                              </div>

                              {showAddRowForm === card.id && (
                                <div className="row-form">
                                  <h4 style={{ marginBottom: '15px' }}>Aggiungi Righe</h4>
                                  
                                  {/* Form per aggiungere una riga */}
                                  <form onSubmit={(e) => handleAddRowToPending(card.id, e)}>
                                    <div className="form-group">
                                      <label>Descrizione *</label>
                                      <input
                                        type="text"
                                        value={rowForm.description}
                                        onChange={(e) => setRowForm({ ...rowForm, description: e.target.value })}
                                        placeholder="Inserisci la descrizione della riga"
                                        required
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={rowForm.isInTotal}
                                          onChange={(e) => setRowForm({ ...rowForm, isInTotal: e.target.checked })}
                                        />
                                        Includi nel totale
                                      </label>
                                    </div>
                                    <div className="form-group">
                                      <label>URL di riferimento</label>
                                      <input
                                        type="url"
                                        value={rowForm.refUrl}
                                        onChange={(e) => setRowForm({ ...rowForm, refUrl: e.target.value })}
                                        placeholder="https://..."
                                      />
                                    </div>
                                    <div className="form-actions">
                                      <button type="submit">+ Aggiungi alla lista</button>
                                    </div>
                                  </form>

                                  {/* Lista delle righe temporanee */}
                                  {(pendingRows[card.id] || []).length > 0 && (
                                    <div className="pending-rows-list" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '6px' }}>
                                      <h5 style={{ marginTop: 0, marginBottom: '10px' }}>
                                        Righe da aggiungere ({(pendingRows[card.id] || []).length})
                                      </h5>
                                      {(pendingRows[card.id] || []).map((row, index) => (
                                        <div key={row.tempId} className="pending-row-item" style={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-between', 
                                          alignItems: 'center',
                                          padding: '8px',
                                          marginBottom: '8px',
                                          backgroundColor: 'white',
                                          borderRadius: '4px',
                                          border: '1px solid #e0e0e0'
                                        }}>
                                          <div>
                                            <strong>{row.description}</strong>
                                            {row.isInTotal && <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>✓ Nel totale</span>}
                                            {row.refUrl && <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>🔗 Link</span>}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemovePendingRow(card.id, row.tempId)}
                                            style={{
                                              backgroundColor: '#d32f2f',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              fontSize: '12px'
                                            }}
                                          >
                                            Rimuovi
                                          </button>
                                        </div>
                                      ))}
                                      <div className="form-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                                        <button 
                                          type="button" 
                                          onClick={() => handleSavePendingRows(card.id)}
                                          style={{
                                            backgroundColor: '#20362C',
                                            color: 'white',
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          💾 Salva tutte le righe ({(pendingRows[card.id] || []).length})
                                        </button>
                                        <button 
                                          type="button" 
                                          onClick={() => handleCancelPendingRows(card.id)}
                                          style={{
                                            backgroundColor: '#666',
                                            color: 'white',
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Annulla
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Pulsante per chiudere se non ci sono righe temporanee */}
                                  {(pendingRows[card.id] || []).length === 0 && (
                                    <div className="form-actions" style={{ marginTop: '10px' }}>
                                      <button type="button" onClick={() => handleCancelPendingRows(card.id)}>
                                        Annulla
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="rows-list">
                                {card.rows
                                  .filter(r => !r.isDeleted)
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map((row, index, filteredRows) => {
                                    const canMoveUp = index > 0;
                                    const canMoveDown = index < filteredRows.length - 1;
                                    
                                    return (
                                  <div key={row.id} className="row-item">
                                    {editingRow === row.id ? (
                                      <form onSubmit={(e) => handleUpdateRow(row.id, e)}>
                                        <div className="form-group">
                                          <label>Descrizione *</label>
                                          <input
                                            type="text"
                                            value={rowForm.description}
                                            onChange={(e) => setRowForm({ ...rowForm, description: e.target.value })}
                                            required
                                          />
                                        </div>
                                        <div className="form-group">
                                          <label>
                                            <input
                                              type="checkbox"
                                              checked={rowForm.isInTotal}
                                              onChange={(e) => setRowForm({ ...rowForm, isInTotal: e.target.checked })}
                                            />
                                            Includi nel totale
                                          </label>
                                        </div>
                                        <div className="form-group">
                                          <label>URL di riferimento</label>
                                          <input
                                            type="url"
                                            value={rowForm.refUrl}
                                            onChange={(e) => setRowForm({ ...rowForm, refUrl: e.target.value })}
                                          />
                                        </div>
                                        <div className="form-actions">
                                          <button type="submit">Salva</button>
                                          <button type="button" onClick={cancelEdit}>Annulla</button>
                                        </div>
                                      </form>
                                    ) : (
                                      <>
                                        <div className="row-info">
                                          <span className="row-description">{row.description}</span>
                                          {row.isInTotal && <span className="row-badge">Nel totale</span>}
                                          {row.refUrl && (
                                            <a href={row.refUrl} target="_blank" rel="noopener noreferrer" className="row-link">
                                              🔗 Link
                                            </a>
                                          )}
                                        </div>
                                        <div className="row-actions">
                                          {!row.isDeleted && (
                                            <>
                                              <div className="row-order-buttons">
                                                <button 
                                                  className="btn-move" 
                                                  onClick={() => handleMoveRow(card.id, row.id, 'up')}
                                                  disabled={!canMoveUp}
                                                  title="Sposta su"
                                                >
                                                  ↑
                                                </button>
                                                <button 
                                                  className="btn-move" 
                                                  onClick={() => handleMoveRow(card.id, row.id, 'down')}
                                                  disabled={!canMoveDown}
                                                  title="Sposta giù"
                                                >
                                                  ↓
                                                </button>
                                              </div>
                                              <button onClick={() => startEditRow(row)}>Modifica</button>
                                              <button onClick={() => handleDeleteRow(row.id)} className="btn-delete">Nascondi</button>
                                            </>
                                          )}
                                          {row.isDeleted && (
                                            <>
                                              <span className="row-status">Riga nascosta - non genererà errori durante l'import</span>
                                              <button onClick={() => handleRestoreRow(row.id)} className="btn-restore">Ripristina</button>
                                            </>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardConfigPanel;

